/**
 * OAuth flow utilities for Cognito Hosted UI
 * Shared across member, admin, and concert-archive portals
 */

import { CognitoConfig } from './cognito-config'
import { parseJwtToken, extractUsername } from './token-utils'

export interface OAuthTokens {
  idToken: string
  accessToken: string
  refreshToken?: string
}

export interface OAuthConfig {
  cognitoConfig: CognitoConfig
  redirectUri: string
  logoutUri?: string
  identityProvider?: 'Google' | 'Facebook' | 'Amazon' | string
  scopes?: string[]
}

// ── PKCE helpers ──────────────────────────────────────────────────────────────

const PKCE_VERIFIER_KEY = 'oauth_pkce_code_verifier'
const OAUTH_STATE_KEY = 'oauth_state'

/**
 * Generate a cryptographically random string of the given byte length,
 * returned as a URL-safe base64 string.
 */
function generateRandomString(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength))
  // Convert to base64url (RFC 7636 appendix A)
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Derive an S256 code challenge from a code verifier (RFC 7636 §4.2).
 */
async function deriveCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Store PKCE verifier and state in sessionStorage before redirect.
 * Uses sessionStorage (not localStorage) so values are scoped to the tab
 * and cleared automatically when the tab closes.
 */
export function storePkceParams(codeVerifier: string, state: string): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier)
  sessionStorage.setItem(OAUTH_STATE_KEY, state)
}

/**
 * Retrieve and clear stored PKCE verifier from sessionStorage.
 */
export function retrieveAndClearPkceVerifier(): string | null {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  return verifier
}

/**
 * Retrieve and clear stored OAuth state from sessionStorage.
 */
export function retrieveAndClearOAuthState(): string | null {
  const state = sessionStorage.getItem(OAUTH_STATE_KEY)
  sessionStorage.removeItem(OAUTH_STATE_KEY)
  return state
}

// ── OAuth URL generation ──────────────────────────────────────────────────────

/**
 * Generate OAuth authorization URL for Cognito Hosted UI.
 *
 * Automatically generates and stores a PKCE code_verifier/code_challenge
 * and a random state parameter for CSRF protection. These are stored in
 * sessionStorage and must be retrieved during the token exchange via
 * `exchangeCodeForTokens()`.
 */
export async function generateOAuthUrl(config: OAuthConfig): Promise<string> {
  const { cognitoConfig, redirectUri, identityProvider, scopes } = config

  if (!cognitoConfig.domain) {
    throw new Error('Cognito domain is required for OAuth flow')
  }

  // Generate PKCE code verifier and challenge (RFC 7636)
  const codeVerifier = generateRandomString(32)
  const codeChallenge = await deriveCodeChallenge(codeVerifier)

  // Generate random state for CSRF protection
  const state = generateRandomString(16)

  // Store both in sessionStorage for retrieval during token exchange
  storePkceParams(codeVerifier, state)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoConfig.clientId,
    redirect_uri: redirectUri,
    scope: (scopes || ['openid', 'email', 'profile']).join(' '),
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    state: state,
  })

  if (identityProvider) {
    params.set('identity_provider', identityProvider)
  }

  return `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/oauth2/authorize?${params.toString()}`
}

/**
 * Generate OAuth logout URL for Cognito Hosted UI
 */
export function generateLogoutUrl(config: OAuthConfig): string {
  const { cognitoConfig, logoutUri } = config

  if (!cognitoConfig.domain) {
    throw new Error('Cognito domain is required for OAuth flow')
  }

  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
  })

  if (logoutUri) {
    params.set('logout_uri', logoutUri)
  }

  return `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/logout?${params.toString()}`
}

// ── Token exchange ────────────────────────────────────────────────────────────

/**
 * Exchange OAuth authorization code for tokens.
 *
 * Automatically retrieves the PKCE code_verifier from sessionStorage and
 * includes it in the token request. Also validates the returned state
 * parameter against the stored value.
 *
 * @param code - The authorization code from the callback URL
 * @param config - OAuth configuration
 * @param callbackState - The state parameter from the callback URL query string
 */
export async function exchangeCodeForTokens(
  code: string,
  config: OAuthConfig,
  callbackState?: string,
): Promise<OAuthTokens> {
  const { cognitoConfig, redirectUri } = config

  if (!cognitoConfig.domain) {
    throw new Error('Cognito domain is required for OAuth flow')
  }

  // Validate state parameter for CSRF protection
  const storedState = retrieveAndClearOAuthState()
  if (callbackState !== undefined) {
    if (!storedState) {
      throw new Error('OAuth state not found in session — possible CSRF attack or expired session')
    }
    if (callbackState !== storedState) {
      throw new Error('OAuth state mismatch — possible CSRF attack')
    }
  }

  // Retrieve PKCE code verifier
  const codeVerifier = retrieveAndClearPkceVerifier()

  const tokenUrl = `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/oauth2/token`

  const body: Record<string, string> = {
    grant_type: 'authorization_code',
    client_id: cognitoConfig.clientId,
    code: code,
    redirect_uri: redirectUri,
  }

  // Include code_verifier if available (PKCE)
  if (codeVerifier) {
    body.code_verifier = codeVerifier
  }

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(body),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Failed to exchange authorization code for tokens: ${response.status} - ${errorText}`,
    )
  }

  const tokens = (await response.json()) as {
    id_token: string
    access_token: string
    refresh_token?: string
  }

  return {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
  }
}

// ── Token storage ─────────────────────────────────────────────────────────────

/**
 * Store OAuth tokens in localStorage using Cognito SDK format
 * This makes tokens accessible via CognitoUserPool.getCurrentUser()
 */
export function storeOAuthTokensInCognitoFormat(tokens: OAuthTokens, clientId: string): void {
  const payload = parseJwtToken(tokens.idToken)
  if (!payload) {
    throw new Error('Failed to parse ID token')
  }

  const username = extractUsername(payload)
  const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`

  localStorage.setItem(`${keyPrefix}.${username}.idToken`, tokens.idToken)
  localStorage.setItem(`${keyPrefix}.${username}.accessToken`, tokens.accessToken)

  if (tokens.refreshToken) {
    localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, tokens.refreshToken)
  }

  localStorage.setItem(`${keyPrefix}.LastAuthUser`, username)
}

/**
 * Clear OAuth tokens from localStorage (both custom and Cognito SDK format)
 */
export function clearOAuthTokens(clientId: string, customPrefix?: string): void {
  // Clear Cognito SDK format tokens
  const lastAuthUser = localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
  )

  if (lastAuthUser) {
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`
    localStorage.removeItem(`${keyPrefix}.${lastAuthUser}.idToken`)
    localStorage.removeItem(`${keyPrefix}.${lastAuthUser}.accessToken`)
    localStorage.removeItem(`${keyPrefix}.${lastAuthUser}.refreshToken`)
    localStorage.removeItem(`${keyPrefix}.LastAuthUser`)
  }

  // Also clear custom format tokens if prefix provided (for migration)
  if (customPrefix) {
    localStorage.removeItem(`${customPrefix}_id_token`)
    localStorage.removeItem(`${customPrefix}_access_token`)
    localStorage.removeItem(`${customPrefix}_refresh_token`)
  }
}
